import { Data, Match, Option } from 'effect';
import * as AsyncResult from 'effect/unstable/reactivity/AsyncResult';
import * as Atom from 'effect/unstable/reactivity/Atom';

import type { Page } from '@vs-shop/domain/pagination';
import type { ProductListingError } from '../../capability/productListing/errors';
import type { FacetSet } from '../../capability/productListing/facetSet';
import type { ListingScopeKey } from '../../capability/productListing/listingScope';
import { type ListingState } from '../../capability/productListing/listingSelection';
import { listingRequestKey } from '../../capability/productListing/listingRequestKey';
import type { ProductSummary } from '../../capability/productSchema';
import { resolveListingFacets, type ListingFacets } from './ListingFacets';
import { collectListingPages } from './ListingPages';
import { HYDRATED_IDLE_TTL_MS } from './hydratedIdleTtl';
import type { ListingQuery } from './makeListingQueries';

export type ListingResult = {
  readonly state: ListingState;
  readonly firstPage: number;
  readonly products: Page<ProductSummary>;
  readonly facets: ListingFacets;
  /** Identifies an appended page that failed after earlier pages succeeded. */
  readonly failedPage: Option.Option<number>;
};

class SessionScope extends Data.Class<{
  readonly scopeKey: ListingScopeKey;
  readonly epoch: number;
}> {}

type ListingPageRunDeps<E> = {
  readonly state: (scopeKey: ListingScopeKey) => Atom.Atom<ListingState>;
  readonly epoch: Atom.Atom<number>;
  readonly pageMode: 'append' | 'replace';
  readonly itemsPerPage: number;
  readonly products: (
    state: ListingState,
    page: number,
    epoch: number
  ) => ListingQuery<Page<ProductSummary>, E>;
  readonly facets: (state: ListingState, epoch: number) => ListingQuery<FacetSet, E>;
  readonly retain: (atom: Atom.Atom<unknown>) => Atom.Atom<unknown>;
  readonly firstPage: (scopeKey: ListingScopeKey) => Atom.Atom<number>;
  readonly append: (scopeKey: ListingScopeKey) => Atom.Writable<ListingState, void>;
};

/** Owns coherent page publication and the lifetime of each Listing page run. */
export const makeListingPageRun = <ER>(deps: ListingPageRunDeps<ProductListingError | ER>) => {
  const pageMode = deps.pageMode;
  const accumulatedSnapshot = Atom.family((_key: string) =>
    Atom.make(Option.none<ListingResult>()).pipe(Atom.setIdleTTL(0))
  );
  const currentForSession = Atom.family(({ scopeKey, epoch }: SessionScope) =>
    Atom.make((get) => {
      const state = get(deps.state(scopeKey));
      const firstPage = get(deps.firstPage(scopeKey));
      const snapshot = accumulatedSnapshot(
        `${epoch}/${firstPage}/${listingRequestKey({ ...state, itemsPerPage: deps.itemsPerPage })}`
      );
      Match.value(pageMode).pipe(
        Match.when('append', () => {
          get.mount(snapshot);
          get(deps.retain(snapshot));
        }),
        Match.when('replace', () => {}),
        Match.exhaustive
      );
      const previousSnapshot = get.once(snapshot);
      const previousResult =
        get.self<AsyncResult.AsyncResult<ListingResult, ProductListingError | ER>>();
      const collected = collectListingPages({
        firstPage,
        requestedPage: state.page,
        pageMode,
        read: (page) => get(deps.products(state, page, epoch).current),
      });
      return Match.value(collected).pipe(
        Match.tag('Pending', () => AsyncResult.waitingFrom(previousResult)),
        Match.tag('Failed', ({ cause, waiting }) =>
          AsyncResult.failureWithPrevious(cause, {
            previous: previousResult,
            waiting,
          })
        ),
        Match.tag(
          'Available',
          ({ products, waiting: productsWaiting, failure: failureCause, failedPage }) => {
            const retained = previousSnapshot.pipe(
              Option.filter(
                () => pageMode === 'append' && (productsWaiting || Option.isSome(failureCause))
              )
            );
            const withFailure = (
              result: AsyncResult.AsyncResult<ListingResult, ProductListingError | ER>
            ) =>
              Option.match(failureCause, {
                onNone: () => result,
                onSome: (cause) =>
                  AsyncResult.failureWithPrevious(cause, {
                    previous: Option.some(result),
                    waiting: result.waiting,
                  }),
              });
            return Option.match(retained, {
              onSome: (previous) =>
                withFailure(AsyncResult.success(previous, { waiting: productsWaiting })),
              onNone: () => {
                const facets = get(deps.facets(state, epoch).current);
                const displayedPage = Option.match(failedPage, {
                  onNone: () => Math.min(state.page, products.page),
                  onSome: (page) => page - 1,
                });
                const displayedState = Match.value(displayedPage === state.page).pipe(
                  Match.when(true, () => state),
                  Match.when(false, () => ({ ...state, page: displayedPage })),
                  Match.exhaustive
                );
                const result = { state: displayedState, firstPage, products, failedPage };
                const facetState = resolveListingFacets(
                  facets,
                  get.self<AsyncResult.AsyncResult<ListingResult, ProductListingError | ER>>().pipe(
                    Option.flatMap(AsyncResult.value),
                    Option.map((previous) => previous.facets)
                  )
                );
                const assembled = AsyncResult.success(
                  { ...result, facets: facetState },
                  {
                    waiting: AsyncResult.match(facets, {
                      onInitial: () => true,
                      onFailure: () => productsWaiting,
                      onSuccess: () => productsWaiting || facets.waiting,
                    }),
                  }
                );
                Match.value(
                  pageMode === 'append' && !assembled.waiting && Option.isNone(failureCause)
                ).pipe(
                  Match.when(true, () => get.set(snapshot, Option.some(assembled.value))),
                  Match.when(false, () => {}),
                  Match.exhaustive
                );
                return withFailure(assembled);
              },
            });
          }
        ),
        Match.exhaustive
      );
    }).pipe(Atom.setIdleTTL(HYDRATED_IDLE_TTL_MS))
  );

  const currentAtom = Atom.family((scopeKey: ListingScopeKey) =>
    Atom.make((get) =>
      get(currentForSession(new SessionScope({ scopeKey, epoch: get(deps.epoch) })))
    ).pipe(Atom.setIdleTTL(HYDRATED_IDLE_TTL_MS))
  );

  const retryFn = Atom.family((scopeKey: ListingScopeKey) =>
    Atom.fnSync((_input: void, get) => {
      const state = get(deps.state(scopeKey));
      const epoch = get(deps.epoch);
      const firstPage = get(deps.firstPage(scopeKey));
      for (let page = firstPage; page <= state.page; page += 1) {
        const query = deps.products(state, page, epoch);
        get.set(query.retry, undefined);
        get(query.retry);
      }
      const query = deps.facets(state, epoch);
      get.set(query.retry, undefined);
      get(query.retry);
    })
  );
  const retryAtom = Atom.family((scopeKey: ListingScopeKey) =>
    Atom.writable(
      () => undefined,
      (context, _command: void) => {
        context.set(retryFn(scopeKey), undefined);
        context.get(retryFn(scopeKey));
      }
    )
  );

  const loadNextPageAtom = Atom.family((scopeKey: ListingScopeKey) =>
    Atom.writable(
      (get) => get(deps.state(scopeKey)),
      (context, _change: void) => {
        Match.value(pageMode).pipe(
          Match.when('replace', () => {}),
          Match.when('append', () => {
            const current = context.get(deps.state(scopeKey));
            const failedPage = AsyncResult.match(context.get(currentAtom(scopeKey)), {
              onInitial: () => Option.none<number>(),
              onFailure: () => Option.none<number>(),
              onSuccess: ({ value }) => value.failedPage,
            });
            Option.match(failedPage, {
              onSome: (page) => {
                const query = deps.products(current, page, context.get(deps.epoch));
                context.set(query.refresh, undefined);
                context.get(query.refresh);
              },
              onNone: () => {
                context.set(deps.append(scopeKey), undefined);
              },
            });
          }),
          Match.exhaustive
        );
      }
    )
  );

  return { current: currentAtom, retry: retryAtom, loadNextPage: loadNextPageAtom };
};
